import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { type FoundContact, outreachAction, type OutreachEnrollment } from '@/lib/outreach';
import { EMAIL_MISSING_LABELS, EMAIL_STATUS_LABELS, ENROLLMENT_LABELS, formatWhen, ORIGIN_LABELS } from './outreach-labels';
import type { OutreachData } from './useOutreach';

interface OutreachProspectDetailProps {
  recordId: string;
  data: OutreachData;
  onError: (message: string) => void;
}

const input = 'min-w-0 flex-1 rounded-lg border border-domino-cardBorder bg-domino-white px-2.5 py-1.5 text-sm text-domino-ink focus:border-domino-aubergine focus:outline-none';

/** One person found: why, where it was read, the address and the phone. */
const ContactLine = ({ contact, data, onError }: { contact: FoundContact; data: OutreachData; onError: (message: string) => void }) => {
  const [typing, setTyping] = useState(false);
  const [email, setEmail] = useState('');
  const act = (body: Record<string, unknown>) => outreachAction(body).then(() => data.reload()).catch((cause: unknown) => onError(cause instanceof Error ? cause.message : 'Action impossible.'));
  const searchingEmail = data.busy('find_email', contact.id);
  const searchingPhone = data.busy('find_phone', contact.id);

  return (
    <li className="rounded-lg border border-domino-cardBorder bg-domino-white p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-domino-ink">
            {contact.fullName}
            {contact.jobTitle && <span className="font-normal text-domino-inkSoft"> · {contact.jobTitle}</span>}
          </p>
          {contact.reason && <p className="text-xs text-domino-inkSoft">{contact.reason}</p>}
          {contact.sourceQuote && (
            <p className="mt-1 text-xs italic text-domino-inkSoft">
              « {contact.sourceQuote} » · {ORIGIN_LABELS[contact.origin]}
              {contact.sourceUrl && (
                <a href={contact.sourceUrl} target="_blank" rel="noreferrer" className="ml-1 inline-flex items-center text-domino-aubergine hover:underline">
                  <ExternalLink className="h-3 w-3" aria-label="Ouvrir la source" />
                </a>
              )}
            </p>
          )}
        </div>
        <div className="flex gap-1">
          {contact.state !== 'chosen' && <Button size="sm" variant="secondary" onClick={() => { void act({ action: 'set_contact_state', contactId: contact.id, state: 'chosen' }); }}>Choisir</Button>}
          {contact.state === 'chosen' && <Button size="sm" variant="ghost" onClick={() => { void act({ action: 'set_contact_state', contactId: contact.id, state: 'proposed' }); }}>Choisie</Button>}
          <Button size="sm" variant="ghost" onClick={() => { void act({ action: 'set_contact_state', contactId: contact.id, state: 'dismissed' }); }}>Écarter</Button>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        {contact.email
          ? <span className="text-domino-ink">{contact.email}{contact.emailStatus && <span className="ml-1 text-xs text-domino-inkSoft">{EMAIL_STATUS_LABELS[contact.emailStatus]}</span>}</span>
          : searchingEmail
            ? <span className="text-xs text-domino-inkSoft">Recherche de l’e-mail…</span>
            : (
              <span className="flex items-center gap-2">
                {contact.emailMissing
                  ? <span className="text-xs text-domino-inkSoft">{EMAIL_MISSING_LABELS[contact.emailMissing]}</span>
                  : data.failed('find_email', contact.id) && <span className="text-xs text-domino-inkSoft">Échec de la recherche</span>}
                {contact.emailMissing !== 'opposed' && <Button size="sm" variant="secondary" onClick={() => { void act({ action: 'find_email', contactIds: [contact.id] }); }}>Trouver l’e-mail</Button>}
                {!typing && <Button size="sm" variant="ghost" onClick={() => setTyping(true)}>Saisir</Button>}
              </span>
            )}
        {contact.phone
          ? <a href={`tel:${contact.phone}`} className="text-domino-aubergine hover:underline">{contact.phone}</a>
          : searchingPhone
            ? <span className="text-xs text-domino-inkSoft">Recherche du téléphone…</span>
            : contact.phoneMissing
              ? <span className="text-xs text-domino-inkSoft">Téléphone {contact.phoneMissing === 'no_provider' ? 'sans fournisseur' : 'introuvable'}</span>
              : <Button size="sm" variant="ghost" onClick={() => { void act({ action: 'find_phone', contactId: contact.id }); }}>Trouver le téléphone</Button>}
      </div>
      {typing && (
        <form className="mt-2 flex gap-2" onSubmit={(event) => {
          event.preventDefault();
          void act({ action: 'set_contact_email', contactId: contact.id, email }).then(() => setTyping(false));
        }}>
          <input type="email" required value={email} onChange={(event) => setEmail(event.target.value)} aria-label="Adresse e-mail" className={input} />
          <Button size="sm" type="submit">Enregistrer</Button>
        </form>
      )}
    </li>
  );
};

const EnrollmentLine = ({ enrollment, data, onError }: { enrollment: OutreachEnrollment; data: OutreachData; onError: (message: string) => void }) => {
  const live = ['drafting', 'review', 'active'].includes(enrollment.status);
  const stop = (reason: 'stopped' | 'replied') => outreachAction({ action: 'stop', enrollmentIds: [enrollment.id], reason })
    .then(() => data.reload()).catch((cause: unknown) => onError(cause instanceof Error ? cause.message : 'Action impossible.'));
  return (
    <div className="rounded-lg border border-domino-cardBorder bg-domino-white p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-domino-ink">{ENROLLMENT_LABELS[enrollment.status]} · {enrollment.toEmail}</p>
        {live && (
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => { void stop('replied'); }}>A répondu</Button>
            <Button size="sm" variant="ghost" onClick={() => { void stop('stopped'); }}>Arrêter</Button>
          </div>
        )}
      </div>
      {enrollment.stopReason && <p className="text-xs text-domino-inkSoft">{enrollment.stopReason}</p>}
      <ol className="mt-2 space-y-1">
        {enrollment.messages.map((message) => (
          <li key={message.id} className="flex flex-wrap justify-between gap-2 text-xs text-domino-inkSoft">
            <span className="text-domino-ink">{message.stepIndex === 0 ? 'Premier e-mail' : `Relance ${message.stepIndex}`}</span>
            <span>
              {message.status === 'sent' && `Envoyé ${formatWhen(message.sentAt)}`}
              {message.status === 'scheduled' && `Prévu ${formatWhen(message.scheduledFor)}${message.error ? ` · ${message.error}` : ''}`}
              {message.status === 'queued' && 'Après le précédent'}
              {message.status === 'draft' && 'À relire'}
              {message.status === 'skipped' && 'Non envoyé'}
              {message.status === 'failed' && (message.error ?? 'Échec')}
              {message.status === 'sending' && 'Envoi…'}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
};

/** Under a row: the people found for the object, adding one by hand, its sequence. */
export const OutreachProspectDetail = ({ recordId, data, onError }: OutreachProspectDetailProps) => {
  const [adding, setAdding] = useState(false);
  const [person, setPerson] = useState({ fullName: '', jobTitle: '', email: '' });
  const contacts = data.contactsOf(recordId);
  const enrollment = data.enrollmentOf(recordId);
  const add = () => outreachAction({ action: 'add_contact', recordId, fullName: person.fullName, jobTitle: person.jobTitle || undefined, email: person.email || undefined })
    .then(() => { setAdding(false); setPerson({ fullName: '', jobTitle: '', email: '' }); return data.reload(); })
    .catch((cause: unknown) => onError(cause instanceof Error ? cause.message : 'Ajout impossible.'));

  return (
    <div className="space-y-3 bg-domino-warm px-4 py-3">
      {enrollment && <EnrollmentLine enrollment={enrollment} data={data} onError={onError} />}
      {contacts.length > 0 && <ul className="space-y-2">{contacts.map((contact) => <ContactLine key={contact.id} contact={contact} data={data} onError={onError} />)}</ul>}
      {adding ? (
        <form className="flex flex-wrap gap-2" onSubmit={(event) => { event.preventDefault(); void add(); }}>
          <input required placeholder="Nom" value={person.fullName} onChange={(event) => setPerson({ ...person, fullName: event.target.value })} className={input} />
          <input placeholder="Poste" value={person.jobTitle} onChange={(event) => setPerson({ ...person, jobTitle: event.target.value })} className={input} />
          <input type="email" placeholder="E-mail" value={person.email} onChange={(event) => setPerson({ ...person, email: event.target.value })} className={input} />
          <Button size="sm" type="submit">Ajouter</Button>
        </form>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => setAdding(true)}>Ajouter une personne</Button>
      )}
    </div>
  );
};
